import React, { useEffect, useState, useRef } from 'react';
import './SettlePayment.css';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const toYMD = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().split('T')[0];
};

const SettlePaymentModal = ({ isOpen, onClose, dueId, academyId, onSuccess }) => {
  const [formData, setFormData] = useState({
    playerName: '',
    academy: '',
    batch: '',
    dueOn: '',
    totalDueAmount: '',
    collectedAmount: '',
    collectionMethod: '',
    remark: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem('auth-token') || '';
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const modalRef = useRef(null);
  const baselineRef = useRef(null);
  const [userId, setUserId] = useState(null);
  const buildSubmitPayload = (fd = formData) => {
    const total = parseFloat(fd.totalDueAmount) || 0;
    const paid = parseFloat(fd.collectedAmount) || 0;
    return {
      dueId,
      academyId,
      paidAmount: paid,
      dueAmount: Math.max(total - paid, 0),
      isSettled: paid >= total && total > 0,
      paymentMethod: fd.collectionMethod || '',
      remark: (fd.remark || '').trim(),
      userId: userId || null,     
      userName: fd.playerName || '',  
    };
  };

  useEffect(() => {
    if (!isOpen) return;

    const fetchPaymentDetails = async () => {
      setLoading(true);
      setError('');

      try {
        const apiUrl = `${process.env.REACT_APP_API_ACADEMY_SETTLE_PAYMENT_DUE}/${academyId}/settle-payment?due_id=${dueId}`;
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };

        const { data } = await axios.get(apiUrl, { headers });
        if (data.success) {
          const paymentDue = data.data?.paymentDue || {};
          const availableMethods = data.data?.paymentMethods || [];
          const playerName = paymentDue.player?.user?.name || '-';
          const academyIdFromApi = data.data?.academyId;
          const academyName = data.data?.academies?.[academyIdFromApi] || '-';
          setUserId(paymentDue.player?.user?.id || null);
          const nextForm = {
            playerName,
            academy: academyName,
            batch: paymentDue.batch?.name || '-',
            dueOn: toYMD(paymentDue.dueFrom) || '',
            totalDueAmount: String(data.data?.dueForm?.dueAmount ?? '0'),
            collectedAmount: '',
            collectionMethod: '',
            remark: ''
          };

          setFormData(nextForm);
          setPaymentMethods(availableMethods);
          baselineRef.current = buildSubmitPayload({
            ...nextForm,
            collectedAmount: '',
            collectionMethod: '',
            remark: '',
          });
        } else {
          setError('Failed to fetch payment details: ' + (data.success_message || 'Unknown error'));
        }
      } catch (err) {
        setError('Error fetching payment details: ' + (err?.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [isOpen, dueId, academyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "collectedAmount") {
      let clean = value.replace(/[^0-9.-]/g, '');
      if (clean.split('.').length > 2) return; 
      if ((clean.match(/-/g) || []).length > 1) return; 
      if (clean.includes('-') && clean.indexOf('-') !== 0) return; 
    
      const [i, d = ''] = clean.replace('-', '').split('.');
      const formatted = (d.length > 2 ? `${i}.${d.slice(0, 2)}` : clean);
      setFormData((p) => ({ ...p, collectedAmount: formatted }));
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const newPayload = buildSubmitPayload(formData);
    const oldPayload = baselineRef.current || {};

    const { digest, html } = buildDiffPreview(oldPayload, newPayload);
    if (!digest.length) {
      Swal.fire({
        icon: "info",
        title: "Nothing to collect",
        text: "No differences detected.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      });
      setIsSubmitting(false);
      return;
    }
    const confirm = await Swal.fire({
      title: "Confirm this collection?",
      html,
      showCancelButton: true,
      confirmButtonText: "Collect",
    });
    if (!confirm.isConfirmed) {
      setIsSubmitting(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiUrl = `${process.env.REACT_APP_API_ACADEMY_SETTLE_PAYMENT_DUE}/${academyId}/settle-payment`;
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };

      const { data } = await axios.post(apiUrl, newPayload, { headers });

      fireAudit({
        entity: "payment_due",
        affected_id: dueId || null,
        affected_user: userId  || null,
        action: "update",
        businessApi: apiUrl,
        businessMethod: "POST",
        oldPayload,
        newPayload,
        section: 'Payment',
        subSection: 'Dues',
        extra: { page: "SettlePayment" },
      });

      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: data.success_message || 'Payment settled successfully!',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });

        baselineRef.current = newPayload;

        setFormData((p) => ({
          ...p,
          collectedAmount: '',
          collectionMethod: '',
          remark: ''
        }));

        onClose && onClose();
        onSuccess && onSuccess();
      } else {
        setError(data.success_message || 'Failed to settle payment.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Error settling payment: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose && onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="settle-payment-container" ref={modalRef}>
        <div className="breadcrumb">
          <Link to="/dues" className="breadcrumb-link">Payment Dues</Link> &gt; Settle Payment
        </div>

        <button className="close-button" onClick={onClose}>×</button>

        {loading && <div className="loading-spinner">Loading...</div>}
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="settle-payment-form">
          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              id="playerName"
              name="playerName"
              className={`walkin-form ${formData.playerName ? 'has-value' : ''}`}
              value={formData.playerName}
              onChange={handleChange}
              placeholder="Enter Player Name"
              disabled
            />
            {formData.playerName && <label htmlFor="playerName">Player Name</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              id="academy"
              name="academy"
              className={`walkin-form ${formData.academy ? 'has-value' : ''}`}
              value={formData.academy}
              onChange={handleChange}
              placeholder="Academy Name"
              disabled
            />
            {formData.academy && <label htmlFor="academy">Academy</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              id="batch"
              name="batch"
              className={`walkin-form ${formData.batch ? 'has-value' : ''}`}
              value={formData.batch}
              onChange={handleChange}
              placeholder="Batch"
              disabled
            />
            {formData.batch && <label htmlFor="batch">Batch</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="date"
              id="dueOn"
              name="dueOn"
              className={`walkin-form ${formData.dueOn ? 'has-value' : ''}`}
              value={formData.dueOn}
              onChange={handleChange}
              placeholder="Due On"
              disabled
            />
            {formData.dueOn && <label htmlFor="dueOn">Due On</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="number"
              id="totalDueAmount"
              name="totalDueAmount"
              className={`walkin-form ${formData.totalDueAmount ? 'has-value' : ''}`}
              value={formData.totalDueAmount}
              onChange={handleChange}
              placeholder="Total Due Amount"
              disabled
            />
            {formData.totalDueAmount && <label htmlFor="totalDueAmount">Total Due Amount</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              id="collectedAmount"
              name="collectedAmount"
              className={`walkin-form ${formData.collectedAmount ? 'has-value' : ''}`}
              value={formData.collectedAmount}
              onChange={handleChange}
              placeholder="Enter Collected Amount"
              required
            />
            {formData.collectedAmount && <label htmlFor="collectedAmount">Collected Amount</label>}
          </div>

          <div className="input-container" style={{ marginBottom: '0px' }}>
            <label className="input-label">Payment Method</label>
            {paymentMethods.length > 0 ? (
              paymentMethods.map((method, index) => (
                <div key={method} className="radio-option">
                  <label htmlFor={`method-${index}`}>
                    <input
                      type="radio"
                      id={`method-${index}`}
                      name="collectionMethod"
                      value={method}
                      checked={formData.collectionMethod === method}
                      onChange={handleChange}
                      required
                    />
                    {method}
                  </label>
                </div>
              ))
            ) : (
              <p>No payment methods available</p>
            )}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <textarea
              id="remark"
              name="remark"
              className={`walkin-form ${formData.remark ? 'has-value' : ''}`}
              value={formData.remark}
              onChange={handleChange}
              placeholder="Enter Remark"
              style={{ border: "1px solid #ccc" }}
              rows={4}
            />
            {formData.remark && <label htmlFor="remark">Remark</label>}
          </div>

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Collect'}
          </button>

        </form>
      </div>
    </div>
  );
};

export default SettlePaymentModal;
